import { Button } from "@miror-ui/components";
import { useAuthModal } from "features/shared/hooks/useAuthModal";
import { useSession } from "next-auth/client";
import { useCallback, useState } from "react";
import { repository } from "repositories";
import {
  BaseModal,
  BaseModalProps,
} from "shared/components/organisms/modal/BaseModal";
import { Loading } from "shared/components/templates/Loading";
import { useErrorMessage } from "shared/hooks/useErrorMessage";
import { useFlashContext } from "shared/hooks/useFlashContext";
import { FortuneTeller } from "shared/types";
import { css } from "twin.macro";

const gradientCss = css({ background: "radial-gradient(#FF4CE1, #FF9892)" });

export interface CallEndNotificationSettingModalProps extends BaseModalProps {
  fortuneTeller: FortuneTeller;
}

export const CallEndNotificationSettingModal = ({
  fortuneTeller,
  onDismiss,
  ...modalProps
}: CallEndNotificationSettingModalProps) => {
  const [processing, setProcessing] = useState(false);

  const [session] = useSession();
  const { showAuthModal } = useAuthModal();
  const { handleAxiosError } = useErrorMessage();
  const { toggleFlash } = useFlashContext();

  const handleEnabledNotification = useCallback(async () => {
    if (!session) {
      showAuthModal();
      return;
    }

    setProcessing(true);

    try {
      await repository.callEndNotification.subscribeCallEndNotification({
        fortuneTellerId: fortuneTeller.id,
      });
      onDismiss();
      toggleFlash({ message: "順番待ち通知の設定が完了しました" });
    } catch (error) {
      onDismiss();
      handleAxiosError(error);
    } finally {
      setProcessing(false);
    }
  }, [
    session,
    showAuthModal,
    fortuneTeller,
    onDismiss,
    toggleFlash,
    handleAxiosError,
  ]);

  return (
    <>
      <BaseModal backgroundOnly onDismiss={onDismiss} {...modalProps}>
        <section
          tw="py-8 rounded-t-3xl font-semibold text-white text-center"
          css={gradientCss}
        >
          <div tw="rounded-[18.5px] bg-white py-1.5 px-2.5 inline-block">
            <p tw="text-[13px] text-[#FF5FCE] mt-0.5">新機能🎉</p>
          </div>

          <h3 tw="text-[25px] mt-[14px]">順番待ち通知</h3>
          <p tw="text-[11px] mt-[14px]">
            通話中の鑑定が終了したらすぐにお知らせします
          </p>
        </section>

        <section tw="py-8 px-4 rounded-b-3xl bg-white text-center">
          <h4 tw="font-base text-base tracking-[.6px]">
            現在、{fortuneTeller.name}先生は鑑定中です📞
          </h4>
          <p tw="font-light text-2xs leading-5 text-black mt-3">
            今すぐ、{fortuneTeller.name}
            先生に相談したい場合は、下記のボタンから現在の鑑定が終了したら通知する設定が可能です。
            前の鑑定通話終了の通知を受け取ってすぐに申し込もう！
          </p>
          <Button
            tw="from-[#FE4FD0] to-[#FE4FD0] mt-6"
            onClick={handleEnabledNotification}
          >
            鑑定が終了したら通知する
          </Button>
        </section>
      </BaseModal>

      {processing && <Loading loading />}
    </>
  );
};
